package com.kinoshazam

import androidx.navigation.NavDestination.Companion.hasRoute
import androidx.navigation.NavHostController
import com.kinoshazam.config.getPlatformConfig
import com.kinoshazam.config.isAndroidTv
import com.kinoshazam.model.Route

class ComposeNavigator() : Navigator {

    private val platformConfig = getPlatformConfig()

    private var navController: NavHostController? = null
    private var modalNavController: NavHostController? = null

    override fun bind(
        navHostController: NavHostController,
        modalNavController: NavHostController,
    ) {
        this.navController = navHostController
        this.modalNavController = modalNavController
    }

    override fun unBind() {
        navController = null
        this.modalNavController = null
    }

    override fun navigateTo(route: Route) {
        val controller = controllerFor(route) ?: return
        if (route is Route.Dialog.Decide) {
            controller.navigate(route) {
                popUpTo("empty") { inclusive = false }
                launchSingleTop = false
            }
            return
        }
        controller.navigate(route)
    }

    override fun navigateTo(route: Route, isClearStack: Boolean) {
        val controller = controllerFor(route) ?: return
        if (!isClearStack) {
            controller.navigate(route)
            return
        }

        controller.navigate(route) {
            // Drop auth / intermediate screens and land on [route].
            // Modal graph start is "empty"; main graph start is Main.
            if (controller === modalNavController) {
                popUpTo(EMPTY_ROUTE) { inclusive = false }
            } else {
                popUpTo<Route.Main> { inclusive = false }
            }
            launchSingleTop = true
        }
    }

    override fun popUpTo(route: Route, isInclusive: Boolean) {
        controllerFor(route)?.navigate(route) {
            popUpTo(route) { inclusive = isInclusive }
            launchSingleTop = true
        }
    }

    override fun back() {
        when {
            // Update is a dialog on the main host and sits above the modal overlay.
            // Prefer it over modal (e.g. Profile) so Cancel/Escape dismiss Update first.
            isUpdateShowing() -> navController?.popBackStack()
            isModalShowing() -> modalNavController?.popBackStack()
            navController?.previousBackStackEntry != null -> navController?.popBackStack()
            else -> Unit
        }
    }

    override fun navigateAndClearStack(route: Route) {
        val controller = controllerFor(route) ?: return
        controller.navigate(route) {
            popUpTo(0) { inclusive = true }
            launchSingleTop = true
        }
    }

    override fun dismissModalAndNavigate(route: Route) {
        if (isModalShowing()) {
            modalNavController?.popBackStack(EMPTY_ROUTE, inclusive = false)
        }
        navigateTo(route)
    }

    /** Whether back() will pop something (modal first, then main). */
    override fun canGoBack(): Boolean =
        isUpdateShowing() || isModalShowing() || navController?.previousBackStackEntry != null

    /**
     * Dialog destinations always use the overlay [modalNavController].
     * Everything else stays on the main stack (AppInfo, AllVideo, Update, …).
     */
    private fun controllerFor(route: Route): NavHostController? {
        val useModal = !platformConfig.platform.isAndroidTv() && route is Route.Dialog
        return if (useModal) modalNavController else navController
    }

    private fun isUpdateShowing(): Boolean =
        navController?.currentBackStackEntry?.destination?.hasRoute<Route.Update>() == true

    private fun isModalShowing(): Boolean =
        modalNavController?.previousBackStackEntry != null

    private companion object {
        const val EMPTY_ROUTE = "empty"
    }
}
